import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
  IconButton,
  Stack,
  Card,
  CardContent,
  TextField,
  FormControlLabel,
  Switch,
  Slider,
} from "@mui/material";
import {
  CloudUpload,
  Download,
  ArrowBack,
  ArrowForward,
  Delete,
  Add,
  TextFields,
} from "@mui/icons-material";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import html2canvas from "html2canvas";
import { Canvas, IText, FabricImage } from "fabric";

const CarouselEditor = () => {
  const [slides, setSlides] = useState([
    {
      id: 1,
      text: "",
      image: null,
      imagePreview: null,
      useCanvas: false,
      canvasData: null,
    },
  ]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [useCanvasMode, setUseCanvasMode] = useState(false);
  const carouselRef = useRef(null);
  const canvasRef = useRef(null);
  const fabricCanvasRef = useRef(null);

  // Initialize Fabric canvas when switching to canvas mode
  useEffect(() => {
    if (useCanvasMode && canvasRef.current && !fabricCanvasRef.current) {
      const canvas = new Canvas(canvasRef.current, {
        width: 800,
        height: 600,
        backgroundColor: "#ffffff",
      });

      fabricCanvasRef.current = canvas;

      // Load existing canvas data if available
      const currentSlideData = slides[currentSlide];
      if (currentSlideData.canvasData) {
        canvas.loadFromJSON(currentSlideData.canvasData, () => {
          canvas.renderAll();
        });
      } else if (currentSlideData.imagePreview) {
        // Load image if exists
        FabricImage.fromURL(currentSlideData.imagePreview).then((img) => {
          img.scaleToWidth(800);
          img.scaleToHeight(600);
          img.selectable = false;
          img.evented = false;
          canvas.backgroundImage = img;
          canvas.requestRenderAll();
        });
      }

      // Save canvas state on modification
      canvas.on("object:modified", saveCanvasState);
      canvas.on("object:added", saveCanvasState);
      canvas.on("object:removed", saveCanvasState);
    }

    return () => {
      if (fabricCanvasRef.current && !useCanvasMode) {
        fabricCanvasRef.current.dispose();
        fabricCanvasRef.current = null;
      }
    };
  }, [useCanvasMode]);

  // Update canvas when slide changes
  useEffect(() => {
    if (fabricCanvasRef.current && useCanvasMode) {
      const canvas = fabricCanvasRef.current;
      const currentSlideData = slides[currentSlide];

      canvas.clear();
      canvas.backgroundColor = "#ffffff";

      if (currentSlideData.canvasData) {
        canvas.loadFromJSON(currentSlideData.canvasData, () => {
          canvas.renderAll();
        });
      } else if (currentSlideData.imagePreview) {
        FabricImage.fromURL(currentSlideData.imagePreview).then((img) => {
          img.scaleToWidth(800);
          img.scaleToHeight(600);
          img.selectable = false;
          img.evented = false;
          canvas.backgroundImage = img;
          canvas.requestRenderAll();
        });
      }
    }
  }, [currentSlide, useCanvasMode]);

  const saveCanvasState = () => {
    if (fabricCanvasRef.current) {
      const canvasData = fabricCanvasRef.current.toJSON();
      const updatedSlides = [...slides];
      updatedSlides[currentSlide].canvasData = canvasData;
      setSlides(updatedSlides);
    }
  };

  // Quill editor modules configuration
  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      [{ font: [] }],
      [{ size: [] }],
      ["bold", "italic", "underline", "strike", "blockquote"],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ indent: "-1" }, { indent: "+1" }],
      [{ align: [] }],
      [{ color: [] }, { background: [] }],
      ["link"],
      ["clean"],
    ],
  };

  const formats = [
    "header",
    "font",
    "size",
    "bold",
    "italic",
    "underline",
    "strike",
    "blockquote",
    "list",
    "bullet",
    "indent",
    "link",
    "color",
    "background",
    "align",
  ];

  // Handle text change
  const handleTextChange = (value) => {
    const updatedSlides = [...slides];
    updatedSlides[currentSlide].text = value;
    setSlides(updatedSlides);
  };

  // Handle image upload
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const updatedSlides = [...slides];
        updatedSlides[currentSlide].image = file;
        updatedSlides[currentSlide].imagePreview = reader.result;
        setSlides(updatedSlides);

        // Update canvas if in canvas mode
        if (fabricCanvasRef.current && useCanvasMode) {
          FabricImage.fromURL(reader.result).then((img) => {
            img.scaleToWidth(800);
            img.scaleToHeight(600);
            img.selectable = false;
            img.evented = false;
            fabricCanvasRef.current.backgroundImage = img;
            fabricCanvasRef.current.requestRenderAll();
            saveCanvasState();
          });
        }
      };
      reader.readAsDataURL(file);
    }
  };

  // Add text to canvas
  const addTextToCanvas = () => {
    if (fabricCanvasRef.current) {
      const text = new IText("Double click to edit", {
        left: 100,
        top: 100,
        fontSize: 24,
        fill: "#000000",
        fontFamily: "Arial",
      });
      fabricCanvasRef.current.add(text);
      fabricCanvasRef.current.setActiveObject(text);
      fabricCanvasRef.current.renderAll();
    }
  };

  // Toggle canvas mode
  const handleCanvasModeToggle = () => {
    const newMode = !useCanvasMode;
    setUseCanvasMode(newMode);
    const updatedSlides = [...slides];
    updatedSlides[currentSlide].useCanvas = newMode;
    setSlides(updatedSlides);
  };

  // Add new slide
  const addSlide = () => {
    const newSlide = {
      id: slides.length + 1,
      text: "",
      image: null,
      imagePreview: null,
      useCanvas: false,
      canvasData: null,
    };
    setSlides([...slides, newSlide]);
    setCurrentSlide(slides.length);
  };

  // Delete current slide
  const deleteSlide = () => {
    if (slides.length > 1) {
      const updatedSlides = slides.filter((_, index) => index !== currentSlide);
      setSlides(updatedSlides);
      setCurrentSlide(Math.max(0, currentSlide - 1));
    }
  };

  // Navigate slides
  const nextSlide = () => {
    if (fabricCanvasRef.current) {
      saveCanvasState();
    }
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    if (fabricCanvasRef.current) {
      saveCanvasState();
    }
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  // Download carousel as HTML
  const downloadAsHTML = async () => {
    // Generate canvas images for canvas slides
    const slidesWithImages = await Promise.all(
      slides.map(async (slide) => {
        if (slide.canvasData && slide.useCanvas) {
          const tempCanvas = new Canvas(null, {
            width: 800,
            height: 600,
          });
          return new Promise((resolve) => {
            tempCanvas.loadFromJSON(slide.canvasData, () => {
              const dataURL = tempCanvas.toDataURL({
                format: "png",
                quality: 1,
              });
              resolve({ ...slide, canvasImageData: dataURL });
            });
          });
        }
        return slide;
      })
    );

    const carouselHTML = `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Carousel Presentation</title>
    <style>
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        
        body {
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            min-height: 100vh;
            display: flex;
            justify-content: center;
            align-items: center;
            padding: 20px;
        }
        
        .carousel-container {
            width: 800px;
            height: 600px;
            background: white;
            border-radius: 20px;
            box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
            overflow: hidden;
            position: relative;
        }
        
        .slide {
            width: 100%;
            height: 100%;
            display: none;
            position: relative;
        }
        
        .slide.active {
            display: block;
        }
        
        .slide-image-full {
            width: 100%;
            height: 100%;
            object-fit: cover;
        }
        
        .slide-canvas {
            width: 100%;
            height: 100%;
            object-fit: contain;
            background: white;
        }
        
        .slide-with-content {
            display: flex;
            flex-direction: column;
            padding: 40px;
            overflow-y: auto;
            height: 100%;
        }
        
        .slide-image {
            width: 100%;
            max-height: 300px;
            object-fit: contain;
            border-radius: 10px;
            margin-bottom: 20px;
            background: #f5f5f5;
        }
        
        .slide-content {
            flex: 1;
            overflow-y: auto;
        }
        
        .slide-content h1, .slide-content h2, .slide-content h3 {
            margin-bottom: 15px;
            color: #333;
        }
        
        .slide-content p {
            line-height: 1.6;
            color: #555;
            margin-bottom: 10px;
        }
        
        .navigation {
            position: absolute;
            bottom: 30px;
            left: 50%;
            transform: translateX(-50%);
            display: flex;
            gap: 15px;
            align-items: center;
            z-index: 100;
        }
        
        .nav-button {
            background: #667eea;
            color: white;
            border: none;
            padding: 12px 24px;
            border-radius: 25px;
            cursor: pointer;
            font-size: 16px;
            transition: all 0.3s ease;
            box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
        }
        
        .nav-button:hover {
            background: #764ba2;
            transform: translateY(-2px);
            box-shadow: 0 6px 20px rgba(118, 75, 162, 0.4);
        }
        
        .nav-button:disabled {
            background: #ccc;
            cursor: not-allowed;
            box-shadow: none;
        }
        
        .slide-indicator {
            background: white;
            padding: 8px 16px;
            border-radius: 20px;
            font-weight: bold;
            color: #667eea;
            box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
        }
        
        .dots {
            position: absolute;
            bottom: 80px;
            left: 50%;
            transform: translateX(-50%);
            display: flex;
            gap: 10px;
            z-index: 100;
        }
        
        .dot {
            width: 12px;
            height: 12px;
            border-radius: 50%;
            background: rgba(255, 255, 255, 0.5);
            cursor: pointer;
            transition: all 0.3s ease;
        }
        
        .dot.active {
            background: white;
            width: 30px;
            border-radius: 6px;
        }

        .ql-align-center { text-align: center; }
        .ql-align-right { text-align: right; }
        .ql-align-justify { text-align: justify; }
        
        .slide-content::-webkit-scrollbar {
            width: 8px;
        }
        
        .slide-content::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 10px;
        }
        
        .slide-content::-webkit-scrollbar-thumb {
            background: #667eea;
            border-radius: 10px;
        }
        
        .slide-content::-webkit-scrollbar-thumb:hover {
            background: #764ba2;
        }
    </style>
</head>
<body>
    <div class="carousel-container">
        ${slidesWithImages
          .map((slide, index) => {
            // Canvas slide
            if (slide.canvasImageData) {
              return `
        <div class="slide ${index === 0 ? "active" : ""}" data-slide="${index}">
            <img src="${slide.canvasImageData}" alt="Slide ${
                index + 1
              }" class="slide-canvas">
        </div>`;
            }
            // Image only (no text)
            else if (
              slide.imagePreview &&
              (!slide.text || slide.text.trim() === "")
            ) {
              return `
        <div class="slide ${index === 0 ? "active" : ""}" data-slide="${index}">
            <img src="${slide.imagePreview}" alt="Slide ${
                index + 1
              }" class="slide-image-full">
        </div>`;
            }
            // Image with text
            else {
              return `
        <div class="slide ${index === 0 ? "active" : ""}" data-slide="${index}">
            <div class="slide-with-content">
                ${
                  slide.imagePreview
                    ? `<img src="${slide.imagePreview}" alt="Slide ${
                        index + 1
                      }" class="slide-image">`
                    : ""
                }
                <div class="slide-content">
                    ${slide.text || "<p>No content added</p>"}
                </div>
            </div>
        </div>`;
            }
          })
          .join("")}
        
        <div class="dots">
            ${slidesWithImages
              .map(
                (_, index) =>
                  `<div class="dot ${
                    index === 0 ? "active" : ""
                  }" onclick="goToSlide(${index})"></div>`
              )
              .join("")}
        </div>
        
        <div class="navigation">
            <button class="nav-button" onclick="prevSlide()">← Previous</button>
            <div class="slide-indicator">
                <span id="current-slide">1</span> / ${slidesWithImages.length}
            </div>
            <button class="nav-button" onclick="nextSlide()">Next →</button>
        </div>
    </div>
    
    <script>
        let currentSlide = 0;
        const slides = document.querySelectorAll('.slide');
        const dots = document.querySelectorAll('.dot');
        const totalSlides = slides.length;
        
        function showSlide(index) {
            slides.forEach(slide => slide.classList.remove('active'));
            dots.forEach(dot => dot.classList.remove('active'));
            
            slides[index].classList.add('active');
            dots[index].classList.add('active');
            document.getElementById('current-slide').textContent = index + 1;
            currentSlide = index;
        }
        
        function nextSlide() {
            const next = (currentSlide + 1) % totalSlides;
            showSlide(next);
        }
        
        function prevSlide() {
            const prev = (currentSlide - 1 + totalSlides) % totalSlides;
            showSlide(prev);
        }
        
        function goToSlide(index) {
            showSlide(index);
        }
        
        document.addEventListener('keydown', (e) => {
            if (e.key === 'ArrowRight') nextSlide();
            if (e.key === 'ArrowLeft') prevSlide();
        });
    </script>
</body>
</html>`;

    const blob = new Blob([carouselHTML], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "carousel-presentation.html";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Download current slide as image
  const downloadSlideAsImage = async () => {
    if (useCanvasMode && fabricCanvasRef.current) {
      const dataURL = fabricCanvasRef.current.toDataURL({
        format: "png",
        quality: 1,
      });
      const link = document.createElement("a");
      link.href = dataURL;
      link.download = `slide-${currentSlide + 1}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else if (carouselRef.current) {
      const canvas = await html2canvas(carouselRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
      });

      const dataURL = canvas.toDataURL("image/png");
      const link = document.createElement("a");
      link.href = dataURL;
      link.download = `slide-${currentSlide + 1}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const currentSlideHasText =
    slides[currentSlide].text && slides[currentSlide].text.trim() !== "";
  const currentSlideHasImage = slides[currentSlide].imagePreview;

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Typography
        variant="h3"
        gutterBottom
        align="center"
        sx={{ mb: 4, fontWeight: "bold" }}
      >
        Rich Text Carousel Editor
      </Typography>

      <Stack direction={{ xs: "column", lg: "row" }} spacing={3}>
        {/* Editor Section */}
        <Paper elevation={3} sx={{ flex: 1, p: 3 }}>
          <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
            Edit Slide {currentSlide + 1}
          </Typography>

          {/* Canvas Mode Toggle */}
          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={useCanvasMode}
                  onChange={handleCanvasModeToggle}
                />
              }
              label="Enable Draggable Text Mode (Canvas)"
            />
            <Typography
              variant="caption"
              display="block"
              color="text.secondary"
            >
              Use canvas mode to position text freely on your images
            </Typography>
          </Box>

          {/* Image Upload */}
          <Box sx={{ mb: 3 }}>
            <Button
              variant="contained"
              component="label"
              startIcon={<CloudUpload />}
              fullWidth
              sx={{ mb: 2 }}
            >
              Upload Image
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={handleImageUpload}
              />
            </Button>
            {slides[currentSlide].imagePreview && !useCanvasMode && (
              <Box
                sx={{
                  width: "100%",
                  height: 200,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  border: "2px dashed #ccc",
                  borderRadius: 2,
                  overflow: "hidden",
                  mb: 2,
                }}
              >
                <img
                  src={slides[currentSlide].imagePreview}
                  alt="Preview"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                  }}
                />
              </Box>
            )}
          </Box>

          {/* Canvas Mode Controls */}
          {useCanvasMode ? (
            <Box sx={{ mb: 3 }}>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<TextFields />}
                onClick={addTextToCanvas}
                fullWidth
                sx={{ mb: 2 }}
              >
                Add Text to Canvas
              </Button>
              <Typography variant="body2" color="text.secondary">
                • Click "Add Text to Canvas" to add draggable text
                <br />
                • Double-click text to edit
                <br />
                • Drag to reposition
                <br />• Delete: Select text and press Delete key
              </Typography>
            </Box>
          ) : (
            /* Rich Text Editor */
            <Box sx={{ mb: 3, "& .quill": { height: 300 } }}>
              <Typography variant="subtitle1" gutterBottom>
                Text Content
              </Typography>
              <ReactQuill
                theme="snow"
                value={slides[currentSlide].text}
                onChange={handleTextChange}
                modules={modules}
                formats={formats}
                style={{ height: "250px", marginBottom: "50px" }}
              />
            </Box>
          )}

          {/* Slide Controls */}
          <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
            <Button
              variant="contained"
              color="success"
              startIcon={<Add />}
              onClick={addSlide}
              fullWidth
            >
              Add Slide
            </Button>
            <Button
              variant="contained"
              color="error"
              startIcon={<Delete />}
              onClick={deleteSlide}
              disabled={slides.length === 1}
              fullWidth
            >
              Delete Slide
            </Button>
          </Stack>
        </Paper>

        {/* Preview Section */}
        <Paper elevation={3} sx={{ flex: 1, p: 3 }}>
          <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
            Preview
          </Typography>

          {/* Canvas or Standard Preview */}
          {useCanvasMode ? (
            <Box
              sx={{
                width: "100%",
                mb: 3,
                display: "flex",
                justifyContent: "center",
                bgcolor: "#f5f5f5",
                borderRadius: 2,
                p: 2,
              }}
            >
              <canvas ref={canvasRef} />
            </Box>
          ) : (
            <Card
              ref={carouselRef}
              sx={{
                width: "100%",
                height: 600,
                mb: 3,
                position: "relative",
                overflow: "hidden",
                bgcolor: currentSlideHasText ? "#f5f5f5" : "#000",
              }}
            >
              {currentSlideHasImage && !currentSlideHasText ? (
                // Full-size image when no text
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <img
                    src={slides[currentSlide].imagePreview}
                    alt={`Slide ${currentSlide + 1}`}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                </Box>
              ) : (
                // Standard layout with text
                <CardContent
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    p: 3,
                    overflow: "auto",
                  }}
                >
                  {slides[currentSlide].imagePreview && (
                    <Box
                      sx={{
                        width: "100%",
                        height: 250,
                        mb: 2,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        bgcolor: "white",
                        borderRadius: 2,
                      }}
                    >
                      <img
                        src={slides[currentSlide].imagePreview}
                        alt={`Slide ${currentSlide + 1}`}
                        style={{
                          maxWidth: "100%",
                          maxHeight: "100%",
                          objectFit: "contain",
                        }}
                      />
                    </Box>
                  )}
                  <Box
                    sx={{ flex: 1, overflow: "auto" }}
                    dangerouslySetInnerHTML={{
                      __html:
                        slides[currentSlide].text || "<p>No content added</p>",
                    }}
                  />
                </CardContent>
              )}

              {/* Navigation Controls */}
              <Box
                sx={{
                  position: "absolute",
                  bottom: 16,
                  left: "50%",
                  transform: "translateX(-50%)",
                  display: "flex",
                  gap: 2,
                  alignItems: "center",
                  bgcolor: "rgba(255, 255, 255, 0.9)",
                  borderRadius: 3,
                  p: 1,
                  zIndex: 10,
                }}
              >
                <IconButton
                  onClick={prevSlide}
                  disabled={currentSlide === 0}
                  color="primary"
                >
                  <ArrowBack />
                </IconButton>
                <Typography
                  variant="body1"
                  sx={{ fontWeight: "bold", minWidth: 50, textAlign: "center" }}
                >
                  {currentSlide + 1} / {slides.length}
                </Typography>
                <IconButton
                  onClick={nextSlide}
                  disabled={currentSlide === slides.length - 1}
                  color="primary"
                >
                  <ArrowForward />
                </IconButton>
              </Box>

              {/* Slide Indicators */}
              <Box
                sx={{
                  position: "absolute",
                  bottom: 60,
                  left: "50%",
                  transform: "translateX(-50%)",
                  display: "flex",
                  gap: 1,
                  zIndex: 10,
                }}
              >
                {slides.map((_, index) => (
                  <Box
                    key={index}
                    onClick={() => {
                      if (fabricCanvasRef.current) {
                        saveCanvasState();
                      }
                      setCurrentSlide(index);
                    }}
                    sx={{
                      width: currentSlide === index ? 24 : 8,
                      height: 8,
                      borderRadius: currentSlide === index ? 1 : "50%",
                      bgcolor:
                        currentSlide === index
                          ? "primary.main"
                          : "rgba(255, 255, 255, 0.7)",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      border: "1px solid rgba(0, 0, 0, 0.1)",
                    }}
                  />
                ))}
              </Box>
            </Card>
          )}

          {/* Download Buttons */}
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Download />}
              onClick={downloadAsHTML}
              fullWidth
            >
              Download as HTML
            </Button>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<Download />}
              onClick={downloadSlideAsImage}
              fullWidth
            >
              Download Slide as Image
            </Button>
          </Stack>
        </Paper>
      </Stack>
    </Container>
  );
};

export default CarouselEditor;
